import React, { Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { IGif, INicheMap } from "services/types";
import { PreviewFeed } from "components/feed/PreviewFeed";
import { RelatedTags } from "components/RelatedTags";
import { PageNotFound, NOT_FOUND, REMOVED_GIF } from "components/page-not-found/PageNotFound";
import { TrendingNiches } from "components/widgets/TrendingNiches";
import { useGifQuery, useSmartRelatedGifsQuery } from "queries";
import { className, getHashValue } from "utils";

import "./RelatedFeed.scss";

import { filterGifsList } from "utils/filterGifsList";
import { filterNichesList } from "utils/filterNichesList";
import { IUserList } from "components/feed/GifList";
import { filterUsersList } from "utils/filterUsersList";
import { ViewRegion } from "utils/metrics";
import { FeedAds } from "components/ads/FeedAds";
import { MobileContentPageScrollKeys } from "components/ads/config/units/contentPageScrollMobile";
import { DesktopContentPageScrollKeys } from "components/ads/config/units/contentPageScrollDesktop";

const MAX_PAGES = 5;

const RELATED_TAGS_POSITION = 0;
const NICHES_POSITION = 8;

const STATUS_OK = 200;
const STATUS_GONE = 410;
const STATUS_NOT_FOUND = 404;

export const RelatedFeed = (props: {
  gif: string;
  className?: string;
  rel?: string;
}) => {
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(1);
  const [gifs, setGifs] = useState<IGif[]>([]);
  const [users, setUsers] = useState<IUserList>({});
  const [tags, setTags] = useState<string[]>([]);
  const [niches, setNiches] = useState<INicheMap>({});
  const [getError, setError] = useState<number>(STATUS_OK);
  const order = getHashValue("order");
  const verified = getHashValue("verified");

  const gif = useGifQuery(props.gif);
  const error: any = gif.error;

  const { data, isSuccess, isError, isLoading } = useSmartRelatedGifsQuery({
    gif: props.gif,
    rel: props.rel ?? "",
    page: page,
    verified: !!verified,
    order: order
  });

  /**
   * Pull information about the author of the gif.
   */
  useEffect(() => {
    if (gif.data && gif.data.user !== null) {
      const newUsers = [gif.data.user];
      setUsers(prev => filterUsersList(prev, newUsers));

      const g = gif.data.gif;
      const u = g.userName;
      const t = g.tags;
      const type = g.type === 2 ? "Pic" : "Gif";

      if (t.length && u) {
        document.title = `${t.join(" ")} Porn ${type} by ${u} | RedGIFs`;
      } else if (g.tags.length && !g.userName) {
        document.title = `${t.join(" ")} Porn ${type} | RedGIFs`;
      } else if (!t.length && u) {
        document.title = `Porn ${type} | RedGIFs`;
      } else {
        document.title = "RedGIFs | Best Porn GIFs and Pics";
      }
    }

    if (gif.data && gif.data.niches) {
      setNiches(prev => filterNichesList(prev, gif.data.niches));
    }

    if (error !== null && (error.response?.status === STATUS_GONE || error.response?.status === STATUS_NOT_FOUND)) {
      setError(error.response.status);
    }
  }, [error, gif.data, gif.status]);

  const hasMore = useMemo(() => page < pages, [page, pages]);

  const fetchMore = useCallback(() => {
    setPage((prevPage) => prevPage + 1);
  }, []);

  const feed = useMemo(() => {
    return gif.isSuccess && gif.data !== undefined
      ? [gif.data.gif, ...gifs]
      : [];
  }, [gif.data, gif.isSuccess, gifs]);

  useEffect(() => {
    if (isSuccess && data !== undefined) {
      setGifs(prev => {
        const newGifs = data.gifs.filter(gif => props.gif !== gif.id);
        return filterGifsList(prev, newGifs);
      });

      setPages(Math.min(MAX_PAGES, data.pages));
      setUsers(prev => filterUsersList(prev, data.users));

      if (data.tags) {
        setTags(data.tags);
      }

      if (data.niches) {
        setNiches(prev => filterNichesList(prev, data.niches));
      }
    }
  }, [data, isSuccess, props.gif]);

  const injector = useCallback((pos: number, gif: IGif, fullscreen: boolean): JSX.Element => {
    const children: ReactNode[] = [];

    if (!fullscreen && pos === RELATED_TAGS_POSITION && tags.length > 0) {
      children.push(<RelatedTags key="RelatedTags" tags={tags} />);
    }

    if (!fullscreen && pos === NICHES_POSITION) {
      children.push(<TrendingNiches key="TrendingNiches" />);
    }

    children.push(
      <FeedAds
        key={`feedAds${gif.id}${pos}`}
        position={pos}
        fullscreen={fullscreen}
        mobileAds={MobileContentPageScrollKeys}
        desktopAds={DesktopContentPageScrollKeys}
      />
    );

    return <Fragment>{children}</Fragment>;
  }, [tags]);

  const renderContent = () => {
    if (isError && getError === STATUS_OK) {
      return <p className="error">Error loading gifs. Please refresh the page.</p>;
    }

    if (getError === STATUS_NOT_FOUND) {
      return <PageNotFound pageType={NOT_FOUND} />;
    }

    if (getError === STATUS_GONE) {
      return <PageNotFound pageType={REMOVED_GIF} />;
    }

    return (
      <PreviewFeed
        gifs={feed}
        users={users}
        niches={niches}
        hasMore={hasMore}
        fetchMore={fetchMore}
        injector={injector}
        source={ViewRegion.Related}
        isLoading={isLoading}
      />
    );
  };

  return (
    <div className={className({
      "relatedFeed": true,
      [props.className ?? ""]: true,
    })}>
      {renderContent()}
    </div>
  );
};
